import type { ComponentProps, ReactNode } from "react";
import { Switch as S, Tabs as T } from "radix-ui";
import { cn, initials } from "@/lib/utils";

export function Switch({ className, ...props }: ComponentProps<typeof S.Root>) {
  return (
    <S.Root
      className={cn(
        "relative inline-flex h-[18px] w-8 shrink-0 cursor-pointer items-center rounded-full border border-border-strong bg-bg-muted transition-colors",
        "data-[state=checked]:border-fg data-[state=checked]:bg-fg disabled:opacity-40",
        className,
      )}
      {...props}
    >
      <S.Thumb className="block h-3 w-3 translate-x-[2px] rounded-full bg-fg-subtle transition-transform data-[state=checked]:translate-x-[15px] data-[state=checked]:bg-fg-inverse" />
    </S.Root>
  );
}

export function SwitchRow({
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string;
  description?: ReactNode;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 py-2">
      <span>
        <span className="block text-[13px] font-medium">{label}</span>
        {description ? <span className="mt-0.5 block text-xs text-fg-muted">{description}</span> : null}
      </span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </label>
  );
}

export const Tabs = T.Root;
export const TabsContent = T.Content;

export function TabsList({ className, ...props }: ComponentProps<typeof T.List>) {
  return <T.List className={cn("flex items-center gap-1 border-b border-border", className)} {...props} />;
}

export function TabsTrigger({ className, ...props }: ComponentProps<typeof T.Trigger>) {
  return (
    <T.Trigger
      className={cn(
        "-mb-px h-9 border-b-2 border-transparent px-2.5 text-[13px] font-medium text-fg-muted transition-colors hover:text-fg",
        "data-[state=active]:border-fg data-[state=active]:text-fg",
        className,
      )}
      {...props}
    />
  );
}

/**
 * Avatar monocromático. A cor do usuário (a única cor da interface) aparece
 * só como anel fino — é ela que liga a pessoa ao cursor no editor.
 */
export function Avatar({
  name,
  src,
  color,
  size = 24,
  ring = false,
  className,
}: {
  name: string;
  src?: string | null;
  color?: string;
  size?: number;
  ring?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-emphasis font-semibold text-fg",
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(9, size * 0.38),
        boxShadow: ring && color ? `0 0 0 2px var(--bg-subtle), 0 0 0 3px ${color}` : undefined,
      }}
      title={name}
    >
      {src ? (
        <img
          src={src}
          alt=""
          className="avatar-photo h-full w-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        initials(name)
      )}
    </span>
  );
}

export function AvatarStack({
  people,
  max = 4,
  size = 24,
}: {
  people: Array<{ id: string; name: string; avatarUrl?: string | null; color?: string }>;
  max?: number;
  size?: number;
}) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((p) => (
        <Avatar key={p.id} name={p.name} src={p.avatarUrl ?? null} color={p.color} size={size} ring />
      ))}
      {rest > 0 ? (
        <span
          className="z-10 inline-flex items-center justify-center rounded-full bg-bg-emphasis text-[10px] font-semibold text-fg-muted ring-2 ring-bg-subtle"
          style={{ width: size, height: size }}
        >
          +{rest}
        </span>
      ) : null}
    </div>
  );
}
